import type { GetArAgingReportResponse } from '@vy/api-definition';
import { getCurrencySymbol } from '@vy/services';
import { sum, withDefault } from '@vy/utils';
import type ExcelJs from 'exceljs';

export function createSummaryWorksheet(workbook: ExcelJs.Workbook, arAgingReport: GetArAgingReportResponse) {
  const summaryWorksheet = workbook.addWorksheet('AR Health as of Date', {
    properties: {
      defaultColWidth: 24,
      tabColor: { argb: '46BDC6' },
    },
    views: [
      {
        zoomScale: 110,
      },
    ],
  });

  summaryWorksheet.addRows([
    ['AR Health'],
    [''],
    ['Currency', arAgingReport.settings.currency],
    [''],
    [''],
    [''],
    ['Days Outstanding', 'Total Gross Amount', 'Number of Invoices'],
    ['1-30'],
    ['31-60'],
    ['61-90'],
    ['91+'],
    ['Total'],
    [''],
    [''],
    ['Customer Name', 'Customer Id', 'Total Gross Amount', '1-30', '31-60', '61-90', '91+'],
  ]);

  arAgingReport.summary.forEach(({ customer, outstandingBreakdown }) => {
    const newRow = summaryWorksheet.addRow([
      customer.name,
      customer.id.slice(-5),
      sum(
        withDefault(outstandingBreakdown.upToOneMonth?.grossAmount, 0),
        withDefault(outstandingBreakdown.oneToTwoMonths?.grossAmount, 0),
        withDefault(outstandingBreakdown.twoToThreeMonths?.grossAmount, 0),
        withDefault(outstandingBreakdown.moreThanThreeMonths?.grossAmount, 0),
      ),
      withDefault(outstandingBreakdown.upToOneMonth?.grossAmount, 0),
      withDefault(outstandingBreakdown.oneToTwoMonths?.grossAmount, 0),
      withDefault(outstandingBreakdown.twoToThreeMonths?.grossAmount, 0),
      withDefault(outstandingBreakdown.moreThanThreeMonths?.grossAmount, 0),
    ]);

    newRow.getCell(1).value = {
      text: customer.name,
      hyperlink: `${window.location.origin}/customers/${customer.id}`,
      tooltip: `Open ${customer.name} in Vayu`,
    };

    newRow.getCell(3).numFmt = `${getCurrencySymbol(arAgingReport.settings.currency)}#,##0.00`;
    newRow.getCell(4).numFmt = '#,##0';
    newRow.getCell(5).numFmt = '#,##0';
    newRow.getCell(6).numFmt = '#,##0';
    newRow.getCell(7).numFmt = '#,##0';

    newRow.commit();
  });

  ['B8', 'B9', 'B10', 'B11'].map((cell, index) => {
    const D_CHAR_ASCII = 68;
    const currentChar = String.fromCharCode(D_CHAR_ASCII + index);

    summaryWorksheet.getCell(cell).value = {
      formula: `SUM(${currentChar}16:${currentChar}${16 + arAgingReport.summary.length - 1})`,
    };
    summaryWorksheet.getCell(cell).numFmt = `${getCurrencySymbol(arAgingReport.settings.currency)}#,##0.00`;
  });

  ['C8', 'C9', 'C10', 'C11'].map((cell, index) => {
    const D_CHAR_ASCII = 68;
    const currentChar = String.fromCharCode(D_CHAR_ASCII + index);

    summaryWorksheet.getCell(cell).value = {
      formula: `COUNTIF(${currentChar}16:${currentChar}${16 + arAgingReport.summary.length - 1}, ">0")`,
    };
    summaryWorksheet.getCell(cell).numFmt = '#,##0';
  });

  summaryWorksheet.getCell('B12').value = {
    formula: 'SUM(B8:B11)',
  };
  summaryWorksheet.getCell('C12').value = {
    formula: 'SUM(C8:C11)',
  };

  summaryWorksheet.getCell('B12').numFmt = `${getCurrencySymbol(arAgingReport.settings.currency)}#,##0.00`;
  summaryWorksheet.getCell('C12').numFmt = '#,##0';

  ['A3', 'B3', 'A7', 'B7', 'C7', 'A15', 'B15', 'C15', 'D15', 'E15', 'F15', 'G15'].map((cell) => {
    summaryWorksheet.getCell(cell).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: '46BDC6' },
    };
    summaryWorksheet.getCell(cell).font = {
      color: { argb: 'FFFFFF' },
    };
    summaryWorksheet.getCell(cell).alignment = {
      vertical: 'middle',
      horizontal: 'center',
    };
  });

  summaryWorksheet.autoFilter = 'A15:G15';
}
